import { logEvent } from "firebase/analytics";
import {
  browserLocalPersistence,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  setPersistence,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import React, { useContext, useEffect, useState } from "react";
import { analytics, auth, firestore } from "../firebase/firebase";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [role, setRole] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  // Add a new state to track if user data is fully loaded
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  // Separate function to fetch and set user data
  const fetchUserData = async (userId) => {
    try {
      const userDoc = await getDoc(doc(firestore, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setRole(userData.role || null);
        setIsAdmin(userData.role === "admin");
        setIsManager(userData.role === "manager");
        setUserDataLoaded(true); // Mark user data as loaded
        return userData;
      }
      setUserDataLoaded(true); // Mark as loaded even if no data found
      return null;
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUserDataLoaded(true); // Mark as loaded even on error
      return null;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          setCurrentUser(user);
          setUserDataLoaded(false); // Reset data loaded flag when user changes

          if (user) {
            const userData = await fetchUserData(user.uid);
            if (userData) {
              logEvent(analytics, "login", {
                method: "magic_link",
                role: userData.role,
              });
            }
          } else {
            setRole(null);
            setIsAdmin(false);
            setIsManager(false);
            setUserDataLoaded(true); // Mark as loaded for logout case
          }

          setLoading(false);
          setAuthInitialized(true);
        });

        return unsubscribe;
      } catch (error) {
        console.error("Auth initialization error:", error);
        setLoading(false);
        setAuthInitialized(true);
        setUserDataLoaded(true); // Ensure userDataLoaded is set even on initialization error
      }
    };

    initializeAuth();
  }, []);

  const sendMagicLink = async (email) => {
    try {
      const usersRef = collection(firestore, "users");
      const userQuery = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(userQuery);

      let isAuthorizedUser = false;
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.role === "admin" || userData.role === "manager") {
          isAuthorizedUser = true;
        }
      });

      const hasAuthorizedUser = await checkIfAuthorizedUserExists();

      if (!hasAuthorizedUser || isAuthorizedUser) {
        const actionCodeSettings = {
          url: window.location.href,
          handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        window.localStorage.setItem("emailForSignIn", email);
        return { isNewAdmin: !hasAuthorizedUser };
      }

      throw new Error(
        "Access denied. Only administrators and managers can log in."
      );
    } catch (error) {
      console.error("Magic link error:", error);
      throw error;
    }
  };

  // Add new function to check if admin or manager exists
  const checkIfAuthorizedUserExists = async () => {
    try {
      const usersRef = collection(firestore, "users");
      const authorizedQuery = query(
        usersRef,
        where("role", "in", ["admin", "manager"]),
        limit(1)
      );
      const querySnapshot = await getDocs(authorizedQuery);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking authorized user existence:", error);
      return false;
    }
  };

  const completeMagicLinkSignIn = async () => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return null;
    }

    const email = window.localStorage.getItem("emailForSignIn");
    if (!email) {
      throw new Error("Email not found. Please try signing in again.");
    }

    try {
      // Set loading true at the beginning of the sign-in process
      setLoading(true);
      setUserDataLoaded(false);
      
      const { user } = await signInWithEmailLink(
        auth,
        email,
        window.location.href
      );
      window.localStorage.removeItem("emailForSignIn");

      const userData = await fetchUserData(user.uid);
      const hasAuthorizedUser = await checkIfAuthorizedUserExists();

      if (
        hasAuthorizedUser &&
        (!userData ||
          (userData.role !== "admin" && userData.role !== "manager"))
      ) {
        await signOut(auth);
        setUserDataLoaded(true); // Ensure we mark data as loaded
        setLoading(false); // Make sure we're not still in loading state
        throw new Error(
          "Access denied. Only administrators and managers can log in."
        );
      }

      // All checks passed, ensure we complete loading state
      setLoading(false);
      return user;
    } catch (error) {
      await signOut(auth).catch(console.error);
      setLoading(false);
      setUserDataLoaded(true);
      const errorMessage = error.message || "Sign-in failed. Please try again.";
      console.error("Sign-in completion error:", error);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      setLoading(true); // Set loading during logout
      await signOut(auth);
      localStorage.clear();
      setRole(null);
      setIsAdmin(false);
      setIsManager(false);
      setUserDataLoaded(true);
      setLoading(false);
      logEvent(analytics, "logout", { user: currentUser?.uid });
    } catch (error) {
      console.error("Logout error:", error);
      setLoading(false);
      throw error;
    }
  };

  const sendNotificationEmail = async (toEmail, message) => {
    try {
      const idToken = currentUser ? await currentUser.getIdToken() : null;
      const functionUrl =
        process.env.REACT_APP_FUNC_API_URL ||
        "https://us-central1-usa-verdicts-and-settlements.cloudfunctions.net";

      const response = await fetch(`${functionUrl}/sendNotificationEmail`, {
        method: "POST",
        mode: "cors",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(idToken && { Authorization: `Bearer ${idToken}` }),
        },
        body: JSON.stringify({ email: toEmail, message }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to send notification: ${errorData}`);
      }

      return true;
    } catch (error) {
      console.error("Error sending notification email:", error);
      throw error;
    }
  };

  const value = {
    currentUser,
    isAdmin,
    role,
    loading: loading || !authInitialized || !userDataLoaded, // Include userDataLoaded in loading state
    sendMagicLink,
    authInitialized,
    completeMagicLinkSignIn,
    logout,
    isManager,
    sendNotificationEmail,
  };

  // Only render children when auth is initialized AND user data is loaded
  return (
    <AuthContext.Provider value={value}>
      {!loading && authInitialized && userDataLoaded && children}
    </AuthContext.Provider>
  );
}

export { AuthContext };